import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'src/api/apiClient';
import { getNameID, setAuthToken } from 'src/utility/auth';
import {
  ADVISOR_OBJECT_KEY,
  getSessionStorageItem,
  setSessionStorageItem,
} from 'src/utility/sessionStorage';

export interface AdvisorData {
  agentID: number;
  userID: number;
  nameID: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  iscName: string;
  firstTimeLogin: boolean;
  premiumGoal: number | null;
  appGoal: number | null;
  role: string;
  ricEmail: string;
  ricWorkPhone: string;
  ricBiography: string;
  ricCalendlyURL: string;
  ricProfileImageURL: string | null;
  aipProfileImageURL: string | null;
  sessionTimeout: string;
  caseManagerBio: string;
  caseManagerCalendlyURL: string;
  caseManagerEmail: string;
  caseManagerFullName: string;
  caseManagerProfileImageURL: string | null;
  caseManagerWorkPhone: string | null;
  enableSearchByFinancialAdvisor: boolean;
  enableSearchByInsured: boolean;
  accessToken: string;
}

const ADVISOR_QUERY_KEY = ['covr', 'advisor'] as const;

interface BootstrapParams {
  oneClickAccountId: number | string | undefined;
  bypassAuthentication: boolean;
  enabled: boolean;
}

function getBypassNameID(): string {
  return (
    new URLSearchParams(window.location.search).get('testNameID') ||
    (import.meta.env.VITE_TEST_NAME_ID as string | undefined) ||
    ''
  );
}

export function getOneClickAccountIdOverride(): string {
  return (
    new URLSearchParams(window.location.search).get('oneClickAccountId') ||
    (import.meta.env.VITE_ONE_CLICK_ACCOUNT_ID as string | undefined) ||
    ''
  );
}

async function fetchAdvisor(params: {
  oneClickAccountId: number | string;
  nameID: string;
  bypassAuthentication: boolean;
}): Promise<AdvisorData> {
  const { oneClickAccountId, nameID, bypassAuthentication } = params;
  const url = `/Client/SSO/Authenticate?oneClickAccountId=${encodeURIComponent(
    String(oneClickAccountId),
  )}&testNameID=${encodeURIComponent(nameID)}`;

  const response = await apiClient.get<AdvisorData>(url);
  const data = response.data;

  setSessionStorageItem(ADVISOR_OBJECT_KEY, data);

  if (bypassAuthentication && data.accessToken) {
    setAuthToken(data.accessToken);
  }

  return data;
}

// Bootstrap hook — fires the SSO/Authenticate request once at app boot
export function useAdvisorBootstrap({
  oneClickAccountId,
  bypassAuthentication,
  enabled,
}: BootstrapParams) {
  const nameID = bypassAuthentication ? getBypassNameID() : getNameID();

  return useQuery({
    queryKey: [...ADVISOR_QUERY_KEY, oneClickAccountId, nameID],
    queryFn: () =>
      fetchAdvisor({
        oneClickAccountId: oneClickAccountId as number | string,
        nameID,
        bypassAuthentication,
      }),
    enabled: enabled && Boolean(oneClickAccountId) && Boolean(nameID),
    staleTime: 30 * 60 * 1000,
  });
}

// Read hook — returns the cached advisor (in sessionStorage as fallback)
export function useAdvisor(): { data: AdvisorData | null } {
  const queryClient = useQueryClient();
  const cached = queryClient.getQueriesData<AdvisorData>({ queryKey: ADVISOR_QUERY_KEY });
  const fromCache = cached.find(([, value]) => value)?.[1];
  const data = fromCache ?? getSessionStorageItem<AdvisorData>(ADVISOR_OBJECT_KEY);
  return { data: data ?? null };
}
